import React, {
  useCallback, useState, useEffect, useRef, useMemo, RefObject, Fragment, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import TableResize from '../../../components/table-resize/table-resize.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import DraggableItem from '../../../components/draggable-item/draggable-item.component';
import LongClickListItem from '../../../components/long-click-list-item/long-click-list-item.component';
import FilteredCell from '../../../components/filtered-cell/filtered-cell.component';
import ContextMenu from '../../../components/context-menu/context-menu.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import ChipLink from '../../../components/chip-link/chip-link.component';
import IconOffsetBlock from '../../../components/icon-offset-block/icon-offset-block.component';
import TableSortLabel from '../../../components/table-sort-label/table-sort-label.component';
import TablePagination from '../../../components/table-pagination/table-pagination.component';
import TextNoWrapBlock from '../../../components/text-no-wrap-block/text-no-wrap-block.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import VisibleCell from '../../../components/visible-cell/visible-cell.component';
import PaddedFlexContainer from '../../../components/padded-flex-container/padded-flex-container.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ActionButton from '../../../components/action-button/action-button.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import List from '../../../components/list/list.component';
import ListItem from '../../../components/list-item/list-item.component';
import ListItemText from '../../../components/list-item-text/list-item-text.component';
import ListItemCheckbox from '../../../components/list-item-checkbox/list-item-checkbox.component';
import ActionButtonNewItem from '../../../components/action-button-new-item/action-button-new-item.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import LastUpdateBlock from '../../app/common/last-update-block/last-update-block.component';
import ContentListContainerOffset
  from '../../app/common/content-list-container-offset/content-list-container-offset.component';
import DealParticipantRow from './deal-participant-row/deal-participant-row.component';
import DealParticipantRowForm from './deal-participant-row-form/deal-participant-row-form.component';
import EditIcon from '../../../icons/edit-icon';
import DeleteItemIcon from '../../../icons/delete-item-icon';
import MoreIcon from '../../../icons/more-icon';
import RemoveIcon from '../../../icons/remove-icon';
import DownloadIcon from '../../../icons/download-icon';
import AddCommentIcon from '../../../icons/add-comment-icon';
import ArrowLinkIcon from '../../../icons/arrow-link-icon';
import FormatService from '../../../services/format-service';
import StructureService from '../../../services/structure-service';
import ScreenService from '../../../services/screen-service';
import useArrowNavigation from '../../../hooks/use-arrow-navigation';
import useScreenSmall from '../../../hooks/use-screen-small';
import useCurrentUserPermissions from '../../../hooks/use-current-user-permissions';
import {
  DEAL_PARTICIPANT_ACCESS_LEVEL_VIEW, DEAL_PARTICIPANT_CREATE, DEAL_PARTICIPANT_DELETE, DEAL_PARTICIPANT_EDIT,
  DEAL_PARTICIPANT_LIST_VIEW, DEAL_PARTICIPANT_COMMENT_LIST_VIEW, DEAL_PARTICIPANT_ATTACHMENT_LIST_VIEW, CONTACT_VIEW,
  DEAL_PARTICIPANT_COMMENT_CREATE, DEAL_PARTICIPANT_APPROVAL_VIEW,
} from '../../../constants/entity/permissions';
import { DATA_DEAL_PARTICIPANT_ID } from '../../../constants/system/data-attributes';
import { TABLE_NAME_DEAL_PARTICIPANT_LIST } from '../../../constants/system/table-names';
import { SETTING_NAME_TABLE_COLUMN_SETTINGS } from '../../../constants/entity/setting-names';
import { FILTER_TYPE_ACCESS_LEVEL, FILTER_TYPE_COMPANY_ID } from '../../../constants/system/filter-types';
import { SORT_TYPE_ASC, SORT_TYPE_DESC } from '../../../constants/system/sort-types';
import { ROUTE_CONTACT } from '../../../constants/system/routes';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';

const DealParticipants: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const HEADER_NAME_ACTIONS: string = 'actions';

  const {
    isCustomizeMode,
    currentUser,
    dealParticipantsData,
    dealParticipantsTotal,
    dealParticipantNewItems,
    dealParticipantGroupList,
    dealParticipantNdaFiles,
    onNdaFileChange,
    onNdaFileDelete,
    accessLevelList,
    contactPersonList,
    contactNotParticipantList,
    isEditMode,
    isNewDeal,
    isSelectMode,
    dealId,
    filters,
    page,
    limit,
    sortParams,
    selectedRows,
    withAllRowsSelected,
    onPageChange,
    onLimitChange,
    onAddNewItem,
    onDeleteClick,
    onSelectRow,
    onLongClick,
    onContactClick,
    onOpenModal,
    onOpenDeleteConfirmModal,
    onSortParamsChange,
    onFileViewClick,
    tableColumnSettings,
    onTableColumnsChange,
    onContactChange,
    dealParticipantActiveIds,
    dealParticipantInlineSaveId,
    onInlineEditModeButtonClick,
    isSaveDisabled,
    isSaving,
    onInlineSave,
    onInlineCancel,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onContactNotParticipantSearch,
    onContactNotParticipantSearchClose,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();
  const tableRef: RefObject<HTMLTableElement> | null = useRef(null);
  const headerCellRefs: RefObject<Record<string, HTMLElement>> = useRef({});
  const inputRefs: RefObject<Record<string, HTMLElement>> = useRef({});
  const permissionList: string[] = useCurrentUserPermissions();

  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [extendedTableFields, setExtendedTableFields]: [TableField[] | null, Function]
    = useState<StateValueDefault>(null);

  const dealParticipantAccessLevelViewPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_ACCESS_LEVEL_VIEW,
  ], []);
  const dealParticipantApprovalViewPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_APPROVAL_VIEW,
  ], []);
  const dealParticipantListViewPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_LIST_VIEW,
  ], []);
  const dealParticipantEditPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_EDIT,
  ], []);
  const dealParticipantCreatePermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_CREATE,
  ], []);
  const dealParticipantCommentListViewPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_COMMENT_LIST_VIEW,
  ], []);
  const dealParticipantAttachmentListViewPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_ATTACHMENT_LIST_VIEW,
  ], []);

  const tableColumnDropDeps: DepsItem[] = useMemo((): DepsItem[] => [
    isCustomizeMode, tableColumnSettings,
  ], [isCustomizeMode, tableColumnSettings]);

  const withAllFiltersEmpty: boolean = !filters[FILTER_TYPE_COMPANY_ID] && !filters[FILTER_TYPE_ACCESS_LEVEL];
  const withButtons: boolean = isEditMode || isSelectMode;
  const withFilters: boolean = !withAllFiltersEmpty && !isEditMode && !isSelectMode && !isCustomizeMode;
  const withPagination: boolean = !isEditMode && !isSelectMode && !isCustomizeMode;

  let commonRowIndex: number = 0;

  const checkIsDealParticipantActive: Function = useCallback((dealParticipantId: number): boolean => (
    dealParticipantActiveIds ? dealParticipantActiveIds.includes(dealParticipantId) : false
  ), [dealParticipantActiveIds]);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const onActionCellClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const openModal: Function = useCallback((dealParticipantItem: DealParticipant, isCommentActive?: boolean): void => {
    if (onOpenModal) {
      onOpenModal(dealParticipantItem, isCommentActive);
    }
  }, []);

  const onLastUpdateColumnClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealParticipantItem: DealParticipant,
  ): void => {
    if (dealParticipantItem) {
      openModal(dealParticipantItem);
    }

    event.stopPropagation();
  }, []);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <AddCommentIcon />,
      text: 'Add Comment',
      permission: DEAL_PARTICIPANT_COMMENT_CREATE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealParticipantId: number): void => {
        if (dealParticipantsData) {
          dealParticipantsData.forEach((dealParticipantItem: DealParticipant): void => {
            if (dealParticipantItem.id === dealParticipantId) {
              openModal(dealParticipantItem, true);
            }
          });
        }
      },
    },
    {
      id: 2,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: DEAL_PARTICIPANT_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealParticipantId: number): void => {
        if (dealParticipantId) {
          onOpenDeleteConfirmModal(event, dealParticipantId);
        }
      },
    },
  ], [dealParticipantsData]);

  const tableDealParticipantListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'group.title',
      title: 'Group',
      isSortable: true,
      isNumber: false,
      isRequired: isEditMode,
      component: (dealParticipant: DealParticipant): ReactElement => (
        <TableCell>
          {dealParticipant.group && dealParticipant.group.name}
        </TableCell>
      ),
    },
    {
      id: 2,
      name: 'contact',
      title: 'Contact',
      isEditModeOnly: true,
      isSortable: false,
      isNumber: false,
      isRequired: isEditMode,
      component: (): ReactElement => <TableCell />,
    },
    {
      id: 3,
      name: 'contact.fullName',
      title: 'Name',
      isSortable: true,
      isNumber: false,
      isSticky: true,
      component: (dealParticipant: DealParticipant): ReactElement => (
        <TableCell
          isText
          isSticky
          isStickyLeft={!isCustomizeMode && !isSelectMode}
          isStickyLeftSelect={!isCustomizeMode && isSelectMode}
        >
          {
            dealParticipant.contact && (
              <FilteredLink
                withNewWindow
                link={!isEditMode && !isSelectMode && !isCustomizeMode
                  ? `${ROUTE_CONTACT}/${dealParticipant.contact.id}` : null}
                permission={CONTACT_VIEW}
              >
                {dealParticipant.contact.fullName}
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'contact.company.name',
      title: 'Company',
      isSortable: true,
      isNumber: false,
      component: (dealParticipant: DealParticipant): ReactElement => (
        <TableCell isText>
          {
            dealParticipant.contact && dealParticipant.contact.company && (
              <ChipLink
                isDisabled={isEditMode || isSelectMode || isCustomizeMode}
                label={dealParticipant.contact.company.name}
                imageLink={dealParticipant.contact.company.photoThumbnail || dealParticipant.contact.company.photo
                  || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  if (dealParticipant.contact.company) {
                    onContactClick(event, dealParticipant.contact.company.id);
                  }
                }}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'contact.email',
      title: 'Email',
      isSortable: true,
      isNumber: false,
      component: (dealParticipant: DealParticipant): ReactElement => (
        <TableCell isText>
          {
            dealParticipant.contact && dealParticipant.contact.email && (
              <TextNoWrapBlock>
                <Tooltip title={dealParticipant.contact.email}>
                  <FilteredLink
                    isEmail
                    link={!isEditMode && !isSelectMode && !isCustomizeMode
                      ? dealParticipant.contact.email : null}
                    permission={CONTACT_VIEW}
                  >
                    {dealParticipant.contact.email}
                  </FilteredLink>
                </Tooltip>
              </TextNoWrapBlock>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'contact.phone',
      title: 'Phone',
      isSortable: true,
      isNumber: false,
      component: (dealParticipant: DealParticipant): ReactElement => (
        <TableCell>
          {
            dealParticipant.contact && dealParticipant.contact.phone && dealParticipant.contact.phoneText && (
              <FilteredLink
                isPhone
                link={!isEditMode && !isSelectMode && !isCustomizeMode
                  ? FormatService.formatCleanIntegerNumberString(dealParticipant.contact.phone) : null}
                phoneExtension={dealParticipant.contact.phoneExtension}
                permission={CONTACT_VIEW}
              >
                {
                  `${dealParticipant.contact.phoneText}`
                  + `${dealParticipant.contact.phoneExtension
                    ? ` ext. ${dealParticipant.contact.phoneExtension}` : ''}`
                }
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'name',
      title: 'Title',
      isSortable: true,
      isNumber: false,
      component: (dealParticipant: DealParticipant): ReactElement => (
        <TableCell isText>
          {dealParticipant.name}
        </TableCell>
      ),
    },
    {
      id: 8,
      name: 'createdDate',
      title: 'Created Date',
      isSortable: true,
      isNumber: false,
      component: (dealParticipant: DealParticipant): ReactElement => (
        <TableCell isText>
          {dealParticipant.createdDate}
        </TableCell>
      ),
    },
    {
      id: 9,
      name: 'accessLevel.title',
      title: 'Access Level',
      permission: DEAL_PARTICIPANT_ACCESS_LEVEL_VIEW,
      isSortable: true,
      isNumber: false,
      component: (dealParticipant: DealParticipant): ReactElement => (
        <CheckAccess permissions={dealParticipantAccessLevelViewPermissions}>
          <TableCell isText>
            {dealParticipant.accessLevel && dealParticipant.accessLevel.title}
          </TableCell>
        </CheckAccess>
      ),
    },
    {
      id: 10,
      name: 'isApproved',
      title: 'Approved',
      isSortable: false,
      isNumber: false,
      component: (dealParticipant: DealParticipant): ReactElement => (
        <TableCell isText>
          {FormatService.formatBooleanValue(dealParticipant.isApproved)}
        </TableCell>
      ),
    },
    {
      id: 11,
      name: 'ndaFile',
      title: 'NDA File',
      isSortable: false,
      isNumber: false,
      permission: DEAL_PARTICIPANT_APPROVAL_VIEW,
      component: (dealParticipant: DealParticipant): ReactElement => (
        <CheckAccess permissions={dealParticipantApprovalViewPermissions}>
          <TableCell isText>
            {
              dealParticipant.ndaFile && (
                <FlexContainer>
                  <FlexContainer>
                    {dealParticipant.ndaFile.name}
                  </FlexContainer>
                  {
                    !isEditMode && !isSelectMode && !isCustomizeMode && (
                      <PaddedFlexContainer>
                        <FilteredLink
                          isExternal
                          link={dealParticipant.ndaFile.link}
                          onMouseDown={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onFileViewClick(event, dealParticipant.ndaFile, dealParticipant.name);
                          }}
                        >
                          <IconOffsetBlock offsetTop={1}>
                            <DownloadIcon />
                          </IconOffsetBlock>
                        </FilteredLink>
                      </PaddedFlexContainer>
                    )
                  }
                </FlexContainer>
              )
            }
          </TableCell>
        </CheckAccess>
      ),
    },
    {
      id: 12,
      name: 'updatedDate',
      title: 'Last Update',
      isSortable: true,
      isNumber: true,
      alignLeft: true,
      component: (dealParticipant: DealParticipant): ReactElement => (
        <TableCell
          isText
          isTextNormal
          onClick={(
            !isEditMode && !isSelectMode && !isCustomizeMode
              ? (event: React.MouseEvent<Element, MouseEvent>): void => {
                onLastUpdateColumnClick(event, dealParticipant);
              } : undefined
          )}
        >
          <LastUpdateBlock
            withNoUpdates
            isDisabled={isEditMode || isSelectMode || isCustomizeMode}
            commentList={dealParticipant.comments}
            attachmentList={dealParticipant.attachments}
            commentListViewPermissions={dealParticipantCommentListViewPermissions}
            attachmentListViewPermissions={dealParticipantAttachmentListViewPermissions}
          />
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 13,
      name: 'actions',
      title: 'Actions',
      isHeaderNameHidden: true,
      isSortable: false,
      isNumber: false,
      isNotFiltered: true,
      isSticky: !isCustomizeMode && !isSelectMode,
      isStickyRight: !isCustomizeMode && !isSelectMode,
      component: (dealParticipant: DealParticipant): ReactElement => (
        <TableCell
          isActions
          isTextRight
          isSticky={!isCustomizeMode && !isSelectMode}
          isStickyRight={!isCustomizeMode && !isSelectMode}
          onClick={onActionCellClick}
        >
          {
            (!isEditMode || !checkIsDealParticipantActive(dealParticipant.id)) && !isSelectMode && !isCustomizeMode && (
              <CheckAccess permissions={dealParticipantEditPermissions}>
                <ActionButton
                  isInline
                  withSmallPadding
                  isHidden={!!isSaving}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onInlineEditModeButtonClick(event, dealParticipant.id);
                  }}
                >
                  <EditIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
          {
            !isSelectMode && !isEditMode && !isCustomizeMode && (
              <CheckAccess
                permissions={actionMenuItems && actionMenuItems.map((
                  actionMenuItem: ActionMenuItem,
                ): string | undefined => (
                  actionMenuItem.permission
                ))}
              >
                <ActionButton
                  isInline
                  withPopup
                  withSmallPadding
                  isHidden={!!isSaving}
                  onClick={openActionMenu}
                  {...{
                    [DATA_DEAL_PARTICIPANT_ID]: dealParticipant.id,
                  }}
                >
                  <MoreIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
        </TableCell>
      ),
    },
  ], [isCustomizeMode, isEditMode, isSelectMode, dealParticipantActiveIds]);

  const columnsTotal: number = tableDealParticipantListFields.length;

  const addNewItem: MouseEventHandlerFunction = useCallback((): void => {
    if (onAddNewItem) {
      onAddNewItem({
        id: StructureService.getNewItemId(dealParticipantNewItems),
        name: '',
        groupId: '',
        contactId: '',
        accessLevel: '',
      });
    }
  }, [dealParticipantsData, dealParticipantNewItems]);

  const selectRow: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    rowId: number,
  ): void => {
    if (onSelectRow) {
      onSelectRow(rowId);
    }

    event.stopPropagation();
  }, [selectedRows]);

  const onRowClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealParticipantItem: DealParticipant,
    isListView?: boolean,
  ): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection && !isCustomizeMode) {
      if (isSelectMode) {
        selectRow(event, dealParticipantItem.id);
      } else if ((!isEditMode && !isNewDeal) || isListView) {
        openModal(dealParticipantItem);
      }
    }
  }, [selectedRows, isCustomizeMode, isSelectMode, isEditMode, isNewDeal]);

  const longClick: Function = useCallback((dealParticipantId: number): void => {
    if (!isSelectMode && !isEditMode && !isCustomizeMode) {
      if (onLongClick) {
        onLongClick();
      }

      onSelectRow(dealParticipantId);
    }
  }, [isSelectMode, isEditMode, isCustomizeMode]);

  const deleteClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealParticipantId: number,
  ): void => {
    if (onDeleteClick) {
      onDeleteClick(dealParticipantId);
    }

    event.stopPropagation();
  }, [dealParticipantNewItems]);

  const checkIsRowSelected: Function = useCallback((rowId: number): boolean => (
    selectedRows ? selectedRows.includes(rowId) : false
  ), [selectedRows]);

  const changePage: ChangePageHandlerFunction = useCallback((
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    pageValue: number,
  ): void => {
    if (onPageChange) {
      onPageChange(pageValue);
    }

    ScreenService.resetScrollPosition();
  }, [page, limit]);

  const changeLimit: ChangeLimitHandlerFunction = useCallback((
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (onLimitChange) {
      onLimitChange((event.target as HTMLInputElement).value);
    }
  }, [page, limit]);

  const sortTableRows: Function = useCallback((columnName: string): void => {
    if (onSortParamsChange) {
      onSortParamsChange(
        columnName,
        sortParams.field === columnName && sortParams.type === SORT_TYPE_DESC ? SORT_TYPE_ASC : SORT_TYPE_DESC,
      );
    }
  }, [sortParams]);

  const onTableColumnSettingsChange: Function = useCallback((
    updatedTableColumnSettings: TableField[],
  ): void => {
    if (updatedTableColumnSettings && updatedTableColumnSettings.length) {
      if (onTableColumnsChange) {
        onTableColumnsChange(updatedTableColumnSettings);
      }

      setExtendedTableFields(
        StructureService.extendOptions(
          tableDealParticipantListFields.filter((tableField: TableField): boolean => !tableField.isEditModeOnly),
          updatedTableColumnSettings,
        ),
      );
    }
  }, [tableDealParticipantListFields]);

  const onDropColumn: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    if (tableColumnSettings) {
      onTableColumnSettingsChange(StructureService.switchListItems(tableColumnSettings, dragIndex, hoverIndex));
    }
  }, [extendedTableFields, tableColumnSettings, ...tableColumnDropDeps]);

  useEffect((): void => {
    if (currentUser && !tableColumnSettings) {
      const tableColumnSettingsCommon: Setting = currentUser.settings
        && currentUser.settings.find((settingItem: Setting): boolean => (
          settingItem.name === SETTING_NAME_TABLE_COLUMN_SETTINGS
        ));

      if (onTableColumnsChange) {
        onTableColumnsChange(
          tableColumnSettingsCommon && tableColumnSettingsCommon.value
          && tableColumnSettingsCommon.value[TABLE_NAME_DEAL_PARTICIPANT_LIST],
          true,
        );
      }
    }
  }, [currentUser]);

  useEffect((): void => {
    if (tableColumnSettings && tableColumnSettings.length && !extendedTableFields) {
      setExtendedTableFields(
        StructureService.extendOptions(tableDealParticipantListFields, tableColumnSettings, isEditMode || isNewDeal),
      );
    }
  }, [tableColumnSettings]);

  useEffect((): void => {
    if (extendedTableFields) {
      setExtendedTableFields(
        StructureService.extendOptions(tableDealParticipantListFields, tableColumnSettings, isEditMode || isNewDeal),
      );
    }
  }, [isEditMode, isNewDeal, isSelectMode, isCustomizeMode, dealParticipantActiveIds]);

  useArrowNavigation(inputRefs);

  return (
    <CheckAccess permissions={dealParticipantListViewPermissions}>
      <CheckAccess permissions={dealParticipantEditPermissions} isEnabled={isEditMode || isNewDeal}>
        {
          dealParticipantsData && !!dealParticipantsData.length
          && extendedTableFields && !!extendedTableFields.length ? (
            <Fragment>
              {
                !isSmallScreen ? (
                  <Card>
                    <ContentListContainerOffset
                      withTabs
                      withButtons={withButtons}
                      withFilters={withFilters}
                      withPagination={withPagination}
                    >
                      <TableResize
                        isActive={isCustomizeMode}
                        isDisabled={isEditMode || isNewDeal}
                        tableRef={tableRef}
                        headerCellRefs={headerCellRefs}
                        tableColumnSettings={tableColumnSettings}
                        onTableColumnsChange={onTableColumnSettingsChange}
                      >
                        <Table withStickyHeader isFullWidth={isEditMode || isNewDeal} ref={tableRef}>
                          <TableHead isHeader>
                            <TableRow>
                              {
                                isSelectMode && (
                                  <TableCell isSticky isStickyLeft />
                                )
                              }
                              {
                                extendedTableFields.map((
                                  tableField: TableField, tableFieldIndex: number,
                                ): ReactElement => (
                                  <VisibleCell
                                    isEditMode={isEditMode || isNewDeal}
                                    key={tableField.id}
                                    tableField={tableField}
                                    permissionList={permissionList}
                                  >
                                    <FilteredCell
                                      columnName={tableField.name}
                                      tableName={TABLE_NAME_DEAL_PARTICIPANT_LIST}
                                      currentUser={currentUser}
                                    >
                                      <CheckAccess permissions={tableField.permission ? [tableField.permission] : []}>
                                        <TableCell
                                          isActions={tableField.name === HEADER_NAME_ACTIONS}
                                          isTextRight={tableField.name === HEADER_NAME_ACTIONS}
                                          isSticky={!isCustomizeMode && tableField.isSticky}
                                          isStickyLeft={!isCustomizeMode && !isSelectMode && tableField.isSticky
                                            && !tableField.isStickyRight}
                                          isStickyLeftSelect={!isCustomizeMode && isSelectMode && tableField.isSticky
                                            && !tableField.isStickyRight}
                                          isStickyLeftHeader={!isCustomizeMode && !isSelectMode && tableField.isSticky
                                            && !tableField.isStickyRight}
                                          isStickyRight={!isCustomizeMode && tableField.isSticky
                                            && tableField.isStickyRight}
                                          isStickyRightHeader={!isCustomizeMode && !isSelectMode
                                            && tableField.isSticky && tableField.isStickyRight}
                                          ref={(ref: HTMLElement): void => {
                                            if (headerCellRefs.current) {
                                              headerCellRefs.current[tableFieldIndex] = ref;
                                            }
                                          }}
                                          sortDirection={sortParams.field === tableField.name && tableField.isSortable
                                            ? sortParams.type : false}
                                        >
                                          {
                                            tableField.isSortable && !isEditMode && !isNewDeal ? (
                                              <DraggableItem
                                                isDisabled={!isCustomizeMode}
                                                itemId={tableField.id}
                                                index={tableFieldIndex}
                                                dropDeps={tableColumnDropDeps}
                                                onDropItem={onDropColumn}
                                              >
                                                <TableSortLabel
                                                  withNoLabel={tableField.isHeaderNameHidden}
                                                  isActive={sortParams.field === tableField.name}
                                                  sortType={sortParams.type}
                                                  onClick={(): void => {
                                                    sortTableRows(tableField.name);
                                                  }}
                                                >
                                                  {tableField.title}
                                                  {tableField.isRequired && <RequiredMark />}
                                                </TableSortLabel>
                                              </DraggableItem>
                                            ) : (
                                              !tableField.isHeaderNameHidden && (
                                                <DraggableItem
                                                  isDisabled={!isCustomizeMode}
                                                  itemId={tableField.id}
                                                  index={tableFieldIndex}
                                                  dropDeps={tableColumnDropDeps}
                                                  onDropItem={onDropColumn}
                                                >
                                                  {tableField.title}
                                                  {tableField.isRequired && <RequiredMark />}
                                                </DraggableItem>
                                              )
                                            )
                                          }
                                        </TableCell>
                                      </CheckAccess>
                                    </FilteredCell>
                                  </VisibleCell>
                                ))
                              }
                            </TableRow>
                          </TableHead>
                          <TableBody
                            isPlain
                            withZebraRows
                            withBorderedCells={!isCustomizeMode}
                            withFixedCellsSizeExtended={isEditMode || isNewDeal}
                          >
                            {
                              dealParticipantsData && dealParticipantsData.map((
                                dealParticipant: DealParticipant,
                              ): ReactElement => {
                                const isSelected: boolean = selectedRows
                                  ? withAllRowsSelected || checkIsRowSelected(dealParticipant.id) : false;
                                const isActive: boolean = checkIsDealParticipantActive(dealParticipant.id);
                                const isInlineSaving: boolean = dealParticipantInlineSaveId !== null
                                  ? dealParticipant.id === dealParticipantInlineSaveId : isSaving;
                                const commonDealParticipantIndex: number = ++commonRowIndex;

                                return (
                                  <DealParticipantRow
                                    isActive={isActive}
                                    isInlineSaving={isInlineSaving}
                                    isSaveDisabled={isSaveDisabled}
                                    isSaving={isSaving}
                                    isSelected={isSelected}
                                    isEditMode={isEditMode}
                                    isSelectMode={isSelectMode}
                                    isCustomizeMode={isCustomizeMode}
                                    isNewDeal={isNewDeal}
                                    key={dealParticipant.id}
                                    dealId={dealId}
                                    inputRefs={inputRefs}
                                    dealParticipantItem={dealParticipant}
                                    dealParticipantIndex={commonDealParticipantIndex}
                                    dealParticipantGroupList={dealParticipantGroupList}
                                    permissionList={permissionList}
                                    ndaFile={dealParticipantNdaFiles && dealParticipantNdaFiles[dealParticipant.id]}
                                    onNdaFileChange={onNdaFileChange}
                                    onNdaFileDelete={onNdaFileDelete}
                                    tableDealParticipantListFields={extendedTableFields}
                                    tableName={TABLE_NAME_DEAL_PARTICIPANT_LIST}
                                    tableColumnSettings={tableColumnSettings}
                                    contactPersonList={contactPersonList}
                                    contactNotParticipantList={contactNotParticipantList}
                                    currentUser={currentUser}
                                    accessLevelList={accessLevelList}
                                    onRowClick={onRowClick}
                                    onLongClick={longClick}
                                    onContactChange={onContactChange}
                                    dealParticipantNewItems={dealParticipantNewItems}
                                    onInlineSave={onInlineSave}
                                    onInlineCancel={onInlineCancel}
                                    onContactPersonSearch={onContactPersonSearch}
                                    onContactPersonSearchClose={onContactPersonSearchClose}
                                    onContactNotParticipantSearch={onContactNotParticipantSearch}
                                    onContactNotParticipantSearchClose={onContactNotParticipantSearchClose}
                                  />
                                );
                              })
                            }
                            {
                              (isEditMode || isNewDeal) && dealParticipantNewItems && !!dealParticipantNewItems.length
                              && dealParticipantNewItems.map((dealParticipant: DealParticipant): ReactElement => {
                                const commonDealParticipantIndex: number = ++commonRowIndex;

                                return (
                                  <DealParticipantRowForm
                                    isNewEntity
                                    isActive={!isNewDeal}
                                    isInlineSaving={dealParticipantInlineSaveId !== null
                                      ? dealParticipant.id === dealParticipantInlineSaveId : isSaving}
                                    isSaving={isSaving}
                                    key={dealParticipant.id}
                                    dealParticipantIndex={commonDealParticipantIndex}
                                    dealId={dealId}
                                    inputRefs={inputRefs}
                                    dealParticipantData={dealParticipant}
                                    dealParticipantGroupList={dealParticipantGroupList}
                                    ndaFile={dealParticipantNdaFiles && dealParticipantNdaFiles[dealParticipant.id]}
                                    onNdaFileChange={onNdaFileChange}
                                    onNdaFileDelete={onNdaFileDelete}
                                    accessLevelList={accessLevelList}
                                    contactPersonList={contactPersonList}
                                    contactNotParticipantList={contactNotParticipantList}
                                    onDeleteClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                      deleteClick(event, dealParticipant.id);
                                    }}
                                    onContactChange={onContactChange}
                                    tableColumnSettings={tableColumnSettings}
                                    currentUser={currentUser}
                                    dealParticipantNewItems={dealParticipantNewItems}
                                    onInlineSave={onInlineSave}
                                    onInlineCancel={onInlineCancel}
                                    isSaveDisabled={isSaveDisabled}
                                    onContactPersonSearch={onContactPersonSearch}
                                    onContactPersonSearchClose={onContactPersonSearchClose}
                                    onContactNotParticipantSearch={onContactNotParticipantSearch}
                                    onContactNotParticipantSearchClose={onContactNotParticipantSearchClose}
                                  />
                                );
                              })
                            }
                            {
                              (isEditMode || isNewDeal) && (
                                <CheckAccess permissions={dealParticipantCreatePermissions}>
                                  <TableRowAddNewItem
                                    isSticky
                                    label="Add New Participant"
                                    colSpan={columnsTotal + 1}
                                    onClick={addNewItem}
                                  />
                                </CheckAccess>
                              )
                            }
                          </TableBody>
                        </Table>
                      </TableResize>
                    </ContentListContainerOffset>
                  </Card>
                ) : (
                  <List>
                    {
                      dealParticipantsData.map((dealParticipant: DealParticipant): ReactElement => {
                        const isSelected: boolean = selectedRows
                          ? withAllRowsSelected || checkIsRowSelected(dealParticipant.id) : false;

                        return (
                          <LongClickListItem
                            isButton={!isSelectMode}
                            isExtended={!isSelectMode}
                            isActive={isSelected}
                            key={dealParticipant.id}
                            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                              onRowClick(event, dealParticipant);
                            }}
                            onLongClick={(): void => {
                              longClick(dealParticipant.id);
                            }}
                          >
                            {
                              isSelectMode && (
                                <ListItemCheckbox isChecked={isSelected} itemId={dealParticipant.id} />
                              )
                            }
                            <ListItemText
                              isBlock
                              subText={!isEditMode && !isNewDeal && dealParticipant.contact
                              && dealParticipant.contact.company && dealParticipant.contact.company.name
                                ? dealParticipant.contact.company.name : ''}
                            >
                              {dealParticipant.contact ? dealParticipant.contact.fullName : ''}
                            </ListItemText>
                            <ListItemText isBlock isBlockRight>
                              {
                                !isEditMode && !isNewDeal ? dealParticipant.group.name : (
                                  <Tooltip title="Open in modal">
                                    <ActionButton
                                      withSmallPadding
                                      onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                        onRowClick(event, dealParticipant, true);
                                      }}
                                    >
                                      <ArrowLinkIcon />
                                    </ActionButton>
                                  </Tooltip>
                                )
                              }
                            </ListItemText>
                          </LongClickListItem>
                        );
                      })
                    }
                    {
                      (isEditMode || isNewDeal) && dealParticipantNewItems
                      && dealParticipantNewItems.map((dealParticipant: DealParticipant): ReactElement => (
                        <ListItem
                          isButton
                          isExtended
                          isActive={!isNewDeal}
                          key={dealParticipant.id}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onRowClick(event, dealParticipant);
                          }}
                        >
                          <ListItemText isBlock>
                            New Participant
                          </ListItemText>
                          <ListItemText isBlock isBlockRight>
                            <Tooltip title="Delete">
                              <ActionButton
                                withSmallPadding
                                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                  deleteClick(event, dealParticipant.id);
                                }}
                              >
                                <RemoveIcon />
                              </ActionButton>
                            </Tooltip>
                            <Tooltip title="Open in modal">
                              <ActionButton
                                withSmallPadding
                                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                  onRowClick(event, dealParticipant, true);
                                }}
                              >
                                <ArrowLinkIcon />
                              </ActionButton>
                            </Tooltip>
                          </ListItemText>
                        </ListItem>
                      ))
                    }
                    {
                      (isEditMode || isNewDeal) && (
                        <CheckAccess permissions={dealParticipantCreatePermissions}>
                          <Card isContainer isSelectable withSmallPadding onClick={addNewItem}>
                            <ActionButtonNewItem withNoHighlight label="Add New Participant" />
                          </Card>
                        </CheckAccess>
                      )
                    }
                  </List>
                )
              }
              {
                !isEditMode && !isNewDeal && !isCustomizeMode && (
                  <TablePagination
                    page={page}
                    limit={limit}
                    totalItems={dealParticipantsTotal}
                    onPageChange={changePage}
                    onLimitChange={changeLimit}
                  />
                )
              }
            </Fragment>
          ) : (
            <NoContentBlock>
              No participants
            </NoContentBlock>
          )
        }
      </CheckAccess>
      <ContextMenu
        menuAnchor={menuAnchor}
        onClose={closeActionMenu}
        actionMenuItems={actionMenuItems}
        dataAttributeEntityId={DATA_DEAL_PARTICIPANT_ID}
      />
    </CheckAccess>
  );
});

export default DealParticipants;
